import React, { useEffect, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowLeft,
  faArrowRight,
} from "@fortawesome/free-solid-svg-icons";
import CaseStudiesCard from "./CaseStudiesCard";
import caseStudiesData from "../../JsonFiles/Projects.json";
import "./CaseStudies.css";

const AUTOPLAY_DELAY = 7000;

const ScrollLink = ({ to, children, ...props }) => {
  const handleClick = () => {
    window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
  };

  return (
    <RouterLink to={to} onClick={handleClick} {...props}>
      {children}
    </RouterLink>
  );
};

const CaseStudies = ({ id = "case-studies" }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);
  const totalCaseStudies = caseStudiesData.length;

  useEffect(() => {
    const motionPreference = window.matchMedia("(prefers-reduced-motion: reduce)");
    const updateMotionPreference = () => {
      setPrefersReducedMotion(motionPreference.matches);
    };

    updateMotionPreference();
    motionPreference.addEventListener?.("change", updateMotionPreference);

    return () => {
      motionPreference.removeEventListener?.("change", updateMotionPreference);
    };
  }, []);

  useEffect(() => {
    if (isPaused || prefersReducedMotion || totalCaseStudies <= 1) {
      return undefined;
    }

    const timer = window.setInterval(() => {
      setCurrentIndex((previous) => (previous + 1) % totalCaseStudies);
    }, AUTOPLAY_DELAY);

    return () => window.clearInterval(timer);
  }, [isPaused, prefersReducedMotion, totalCaseStudies]);

  if (totalCaseStudies === 0) return null;

  const showPrevious = () => {
    setCurrentIndex((previous) =>
      previous === 0 ? totalCaseStudies - 1 : previous - 1,
    );
  };

  const showNext = () => {
    setCurrentIndex((previous) => (previous + 1) % totalCaseStudies);
  };

  const handleKeyDown = (event) => {
    if (event.key === "ArrowLeft") {
      event.preventDefault();
      showPrevious();
    }

    if (event.key === "ArrowRight") {
      event.preventDefault();
      showNext();
    }
  };

  return (
    <section
      id={id}
      className="case-studies-section"
      aria-labelledby="case-studies-title"
    >
      <div className="case-studies-section__decoration" aria-hidden="true" />

      <div className="case-studies-section__container">
        <header className="case-studies-section__header">
          <div>
            <p className="case-studies-section__eyebrow">
              <span aria-hidden="true" />
              Selected work
            </p>

            <h2 id="case-studies-title" className="case-studies-section__title">
              Digital products that solve
              <span> real business problems.</span>
            </h2>
          </div>

          <div className="case-studies-section__header-side">
            <p>
              Explore how we turn complex requirements into useful, scalable
              products through focused strategy, design, and engineering.
            </p>

            {totalCaseStudies > 1 && (
              <div className="case-studies-section__controls">
                <button
                  type="button"
                  onClick={showPrevious}
                  aria-label="Show previous case study"
                >
                  <FontAwesomeIcon icon={faArrowLeft} aria-hidden="true" />
                </button>

                <button
                  type="button"
                  onClick={showNext}
                  aria-label="Show next case study"
                >
                  <FontAwesomeIcon icon={faArrowRight} aria-hidden="true" />
                </button>
              </div>
            )}
          </div>
        </header>

        <div
          className="case-studies-section__carousel"
          role="region"
          aria-roledescription="carousel"
          aria-label="Axiomos case studies"
          tabIndex={0}
          onKeyDown={handleKeyDown}
          onMouseEnter={() => setIsPaused(true)}
          onMouseLeave={() => setIsPaused(false)}
          onFocusCapture={() => setIsPaused(true)}
          onBlurCapture={() => setIsPaused(false)}
        >
          <div
            className="case-studies-section__track"
            style={{ transform: `translateX(-${currentIndex * 100}%)` }}
          >
            {caseStudiesData.map((caseStudy, index) => (
              <div
                key={caseStudy.id ?? caseStudy.title ?? index}
                className="case-studies-section__slide"
                aria-hidden={index !== currentIndex}
              >
                <CaseStudiesCard
                  caseStudy={caseStudy}
                  sequence={index + 1}
                  isActive={index === currentIndex}
                />
              </div>
            ))}
          </div>
        </div>

        <div className="case-studies-section__footer">
          <div className="case-studies-section__progress">
            <p aria-live="polite">
              <strong>{String(currentIndex + 1).padStart(2, "0")}</strong>
              <span aria-hidden="true" />
              {String(totalCaseStudies).padStart(2, "0")}
            </p>

            {totalCaseStudies > 1 && (
              <div aria-label="Select case study">
                {caseStudiesData.map((caseStudy, index) => (
                  <button
                    key={caseStudy.id ?? caseStudy.title ?? index}
                    type="button"
                    className={index === currentIndex ? "is-active" : ""}
                    onClick={() => setCurrentIndex(index)}
                    aria-label={`Show case study ${index + 1}`}
                    aria-current={index === currentIndex ? "true" : undefined}
                  />
                ))}
              </div>
            )}
          </div>

          <ScrollLink className="case-studies-section__all-link" to="/projects">
            View all case studies
            <FontAwesomeIcon icon={faArrowRight} aria-hidden="true" />
          </ScrollLink>
        </div>
      </div>
    </section>
  );
};

export default CaseStudies;
